// Sentry-style global DB error reporter.
// Wraps the Supabase client so every failed insert/update/upsert/delete
// pings Evan via the report-db-error edge function.
//
// Imported by src/integrations/supabase/client.ts — do not import directly.

import type { SupabaseClient } from "@supabase/supabase-js";

type Op = "insert" | "update" | "upsert" | "delete";

const REPORT_FN = "report-db-error";

// Truncate large payloads so we never blow up the email
function preview(value: unknown): unknown {
  try {
    const json = JSON.stringify(value);
    if (!json) return value;
    if (json.length <= 4000) return value;
    return JSON.parse(json.slice(0, 4000) + '"…(truncated)"}');
  } catch {
    return String(value).slice(0, 4000);
  }
}

async function fireAlert(
  client: SupabaseClient,
  table: string,
  operation: Op,
  payload: unknown,
  error: { message?: string; code?: string; details?: string; hint?: string; status?: number }
) {
  try {
    await client.functions.invoke(REPORT_FN, {
      body: {
        table,
        operation,
        error_message: error?.message ?? "Unknown error",
        error_code: error?.code ?? null,
        error_details: error?.details ?? null,
        error_hint: error?.hint ?? null,
        status: error?.status ?? null,
        url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        payload_preview: preview(payload),
        timestamp: new Date().toISOString(),
      },
    });
  } catch (e) {
    // Never let the reporter itself break the app
    console.warn("[errorReporter] failed to send alert:", e);
  }
}

// Wraps a PostgrestQueryBuilder so .insert / .update / .upsert / .delete
// are intercepted and any returned `.error` triggers an alert.
function wrapBuilder(client: SupabaseClient, builder: any, table: string) {
  return new Proxy(builder, {
    get(target, prop, receiver) {
      const orig = Reflect.get(target, prop, receiver);
      if (typeof orig !== "function") return orig;

      const opName = prop as string;
      const isWrite =
        opName === "insert" || opName === "update" || opName === "upsert" || opName === "delete";

      if (!isWrite) {
        // Method may return another builder — bind & return as-is
        return orig.bind(target);
      }

      return function (this: any, ...args: any[]) {
        const result = orig.apply(target, args);
        const payload = args[0];

        // PostgREST builders are thenable. Patch .then so we observe the response.
        if (result && typeof result.then === "function") {
          const originalThen = result.then.bind(result);
          result.then = function (
            onFulfilled?: (v: any) => any,
            onRejected?: (e: any) => any
          ) {
            return originalThen(
              (response: any) => {
                if (response && response.error) {
                  fireAlert(client, table, opName as Op, payload, response.error);
                }
                return onFulfilled ? onFulfilled(response) : response;
              },
              (err: any) => {
                fireAlert(client, table, opName as Op, payload, {
                  message: err?.message ?? String(err),
                });
                if (onRejected) return onRejected(err);
                throw err;
              }
            );
          };
        }
        return result;
      };
    },
  });
}

// Wraps the top-level client so every `supabase.from(table)` returns a
// monitored builder. All other methods (auth, functions, channel, storage,
// rpc, etc.) are passed through untouched.
export function withErrorReporter(client: SupabaseClient): SupabaseClient {
  const originalFrom = client.from.bind(client);
  (client as any).from = (table: string) => {
    const builder = originalFrom(table);
    return wrapBuilder(client, builder, table);
  };
  return client;
}
